import {z} from 'zod';

import {promptBridgeApi} from '@dms/api/base';

import {
  DeadStockQuerySchema,
  DeadStockResponseSchema,
  DeadStockSnapshotQuerySchema,
  DeadStockSnapshotResponseSchema,
  InventoryValueQuerySchema,
  InventoryValueResponseSchema,
  InventoryTurnoverQuerySchema,
  InventoryTurnoverResponseSchema,
  WarehouseMovementsQuerySchema,
  WarehouseMovementsResponseSchema,
  type DeadStockQuery,
  type DeadStockResponse,
  type DeadStockSnapshotQuery,
  type DeadStockSnapshotResponse,
  type InventoryValueResponse,
  type InventoryTurnoverResponse,
  type WarehouseMovementsQuery,
  type WarehouseMovementsResponse,
} from '@prompt-bridge/shared/validations';

import {buildDeadStockSnapshotUrl} from './buildDeadStockSnapshotUrl';
import {buildDeadStockUrl} from './buildDeadStockUrl';
import {inventoryTurnoverSearchParams} from './inventoryTurnoverSearchParams';
import {inventoryValueSearchParams} from './inventoryValueSearchParams';
import {mergeDeadStockSnapshotPage} from './mergeDeadStockSnapshotPage';
import type {
  AverageOrderPriceByMonthResponse,
  CountsByMonthResponse,
  DirectSalesByMonthResponse,
  DirectSalesFilterOptions,
  DirectSalesFilterParams,
  DiscountByMonthResponse,
  MarginByMonthResponse,
  MechanicFilterOptions,
  MechanicFilterParams,
  MechanicLabourByMonthResponse,
  ReportFilterOptions,
  ReportFilterParams,
  RevenueByMonthResponse,
  VehicleServiceTimeByMonthResponse,
  InProgressOrdersByMonthResponse,
} from './types';
import {warehouseMovementsSearchParams} from './warehouseMovementsSearchParams';

const reportingEndpoints = promptBridgeApi.injectEndpoints({
  endpoints: (build) => ({
    getDeadStock: build.query<DeadStockResponse, DeadStockQuery>({
      query: (params) => ({url: buildDeadStockUrl(params), queryArg: params}),
      extraOptions: {
        requestSchema: DeadStockQuerySchema,
        responseSchema: DeadStockResponseSchema,
      },
    }),
    getInventoryTurnover: build.query<
      InventoryTurnoverResponse,
      z.input<typeof InventoryTurnoverQuerySchema>
    >({
      query: (params) => ({
        url: `/api/v1/report/inventory-turnover?${inventoryTurnoverSearchParams(params)}`,
        queryArg: params,
      }),
      extraOptions: {
        requestSchema: InventoryTurnoverQuerySchema,
        responseSchema: InventoryTurnoverResponseSchema,
      },
    }),
    getDeadStockSnapshot: build.query<DeadStockSnapshotResponse, DeadStockSnapshotQuery>({
      query: (params) => ({url: buildDeadStockSnapshotUrl(params), queryArg: params}),
      // `offset` is deliberately NOT part of the cache key: every page of one filter set
      // accumulates into a single entry via `merge`, so "show more" appends to the list
      // instead of replacing it. Changing any other filter starts a new entry.
      serializeQueryArgs: ({endpointName, queryArgs}) =>
        // Multi-select values arrive in click order, so they are sorted here - otherwise
        // picking [a, b] and [b, a] would be two entries for identical data.
        `${endpointName}_${JSON.stringify({
          ...queryArgs,
          branch: queryArgs.branch ? [...queryArgs.branch].sort() : undefined,
          warehouse: queryArgs.warehouse ? [...queryArgs.warehouse].sort() : undefined,
          manufacturer: queryArgs.manufacturer ? [...queryArgs.manufacturer].sort() : undefined,
          offset: undefined,
        })}`,
      merge: (currentCache, newData, {arg}) =>
        mergeDeadStockSnapshotPage(currentCache, newData, arg.offset),
      forceRefetch: ({currentArg, previousArg}) =>
        (currentArg?.offset ?? 0) !== (previousArg?.offset ?? 0),
      extraOptions: {
        requestSchema: DeadStockSnapshotQuerySchema,
        responseSchema: DeadStockSnapshotResponseSchema,
      },
    }),
    getWarehouseMovements: build.query<WarehouseMovementsResponse, WarehouseMovementsQuery>({
      query: (params) => ({
        url: `/api/v1/report/warehouse-movements?${warehouseMovementsSearchParams(params)}`,
        queryArg: params,
      }),
      extraOptions: {
        requestSchema: WarehouseMovementsQuerySchema,
        responseSchema: WarehouseMovementsResponseSchema,
      },
    }),
    getInventoryValue: build.query<
      InventoryValueResponse,
      z.input<typeof InventoryValueQuerySchema>
    >({
      query: (params) => ({
        url: `/api/v1/report/inventory-value?${inventoryValueSearchParams(params)}`,
        queryArg: params,
      }),
      extraOptions: {
        requestSchema: InventoryValueQuerySchema,
        responseSchema: InventoryValueResponseSchema,
      },
    }),
    getRevenueByMonth: build.query<RevenueByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/revenue-by-month',
        params,
      }),
    }),
    getAverageOrderPriceByMonth: build.query<AverageOrderPriceByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/average-order-price-by-month',
        params,
      }),
    }),
    getMarginByMonth: build.query<MarginByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/margin-by-month',
        params,
      }),
    }),
    getDiscountByMonth: build.query<DiscountByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/discount-by-month',
        params,
      }),
    }),
    getCountsByMonth: build.query<CountsByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/counts-by-month',
        params,
      }),
    }),
    getVehicleServiceTimeByMonth: build.query<
      VehicleServiceTimeByMonthResponse,
      ReportFilterParams
    >({
      query: (params) => ({
        url: '/api/v1/report/vehicle-service-time-by-month',
        params,
      }),
    }),
    getInProgressOrdersByMonth: build.query<InProgressOrdersByMonthResponse, ReportFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/in-progress-orders-by-month',
        params,
      }),
    }),
    getReportFilterOptions: build.query<ReportFilterOptions, void>({
      query: () => ({
        url: '/api/v1/report/filter-options',
      }),
    }),
    getMechanicLabourByMonth: build.query<MechanicLabourByMonthResponse, MechanicFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/mechanic-labour-by-month',
        params,
      }),
    }),
    getMechanicFilterOptions: build.query<MechanicFilterOptions, void>({
      query: () => ({
        url: '/api/v1/report/mechanic-filter-options',
      }),
    }),
    getDirectSalesByMonth: build.query<DirectSalesByMonthResponse, DirectSalesFilterParams>({
      query: (params) => ({
        url: '/api/v1/report/direct-sales-by-month',
        params,
      }),
    }),
    getDirectSalesFilterOptions: build.query<DirectSalesFilterOptions, void>({
      query: () => ({
        url: '/api/v1/report/direct-sales-filter-options',
      }),
    }),
  }),
});

export const {
  useGetDeadStockQuery,
  useGetDeadStockSnapshotQuery,
  useGetWarehouseMovementsQuery,
  useGetInventoryValueQuery,
  useGetInventoryTurnoverQuery,
  useGetRevenueByMonthQuery,
  useGetAverageOrderPriceByMonthQuery,
  useGetMarginByMonthQuery,
  useGetDiscountByMonthQuery,
  useGetCountsByMonthQuery,
  useGetVehicleServiceTimeByMonthQuery,
  useGetInProgressOrdersByMonthQuery,
  useGetReportFilterOptionsQuery,
  useGetMechanicLabourByMonthQuery,
  useGetMechanicFilterOptionsQuery,
  useGetDirectSalesByMonthQuery,
  useGetDirectSalesFilterOptionsQuery,
} = reportingEndpoints;
